import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const BROWSE_PATH = '/browse';
const SCROLL_KEY = 'ck_browse_scroll_y';

const ScrollToTop: React.FC = () => {
  const location = useLocation();
  const navType = useNavigationType(); // POP when using browser back/forward
  const hasRestoredRef = useRef(false);

  // Disable browser scroll restoration so SPA controls it
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const original = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => {
        try {
          window.history.scrollRestoration = original;
        } catch {
          // no-op
        }
      };
    }
    return undefined;
  }, []);

  // Save scroll position when leaving /browse
  useEffect(() => {
    const onBeforeUnload = () => {
      if (location.pathname === BROWSE_PATH) {
        sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [location.pathname]);

  useEffect(() => {
    // If we're navigating back to /browse (POP) restore previous scroll
    if (location.pathname === BROWSE_PATH && navType === 'POP') {
      const saved = sessionStorage.getItem(SCROLL_KEY);
      const y = saved ? parseInt(saved, 10) : 0;
      // Delay to allow content to paint
      requestAnimationFrame(() => {
        window.scrollTo({ top: isNaN(y) ? 0 : y, left: 0, behavior: 'auto' });
        hasRestoredRef.current = true;
      });
      return undefined;
    }

    // For all other navigations, start at top
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    hasRestoredRef.current = false;
    return undefined;
  }, [location.pathname, navType]);

  // Also save scroll during browsing for better accuracy
  useEffect(() => {
    if (location.pathname !== BROWSE_PATH) return;
    const onScroll = () => {
      sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  return null;
};

export default ScrollToTop; 